import { ResponsiveData, Section, DeviceData } from "@/types/models/page";
import {
  ComponentConfig,
  ComponentDefinition,
} from "@/types/models/componentConfig";

export type DeviceType = "desktop" | "tablet" | "mobile";

export interface ChangeHistoryEntry {
  fieldKey: string;
  oldValue: any;
  newValue: any;
  device: DeviceType;
  timestamp: string;
}

class SectionDataService {
  private changeHistory = new Map<string, ChangeHistoryEntry[]>();
  private maxHistorySize = 100;

  private deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }
    return JSON.parse(JSON.stringify(obj));
  }

  private getComponentDataMap(
    section: Section,
  ): Record<string, ResponsiveData> {
    if (!section.component_data) {
      return {};
    }
    if (Array.isArray(section.component_data)) {
      return this.convertArrayToMap(section.component_data as any[]);
    }
    return section.component_data;
  }

  private convertArrayToMap(arrayData: any[]): Record<string, ResponsiveData> {
    const map: Record<string, ResponsiveData> = {};
    arrayData.forEach((item, index) => {
      // Try different possible key sources
      const key =
        item.key || item.name || item.componentSlug || `component-${index}`;
      if (key) {
        map[key] = item;
      }
    });
    return map;
  }

  private createComponentMapping(section: Section): Map<string, string[]> {
    const mapping = new Map<string, string[]>();
    const componentData = this.getComponentDataMap(section);

    for (const dataKey in componentData) {
      const slug = componentData[dataKey]?.componentSlug;
      if (slug) {
        if (!mapping.has(slug)) {
          mapping.set(slug, []);
        }
        mapping.get(slug)!.push(dataKey);
      }
    }
    return mapping;
  }

  private createFieldKey(componentSlug: string, configTitle: string): string {
    return `${componentSlug}::${configTitle}`;
  }

  private parseFieldKey(fieldKey: string): {
    componentSlug: string;
    configTitle: string;
  } {
    const parts = fieldKey.split("::");
    return { componentSlug: parts[0], configTitle: parts[1] };
  }

  private getDeviceData(
    component: ResponsiveData,
    device: DeviceType,
  ): DeviceData {
    switch (device) {
      case "desktop":
        return component.desktop;
      case "tablet":
        return component.tablet;
      case "mobile":
        return component.mobile;
    }
  }
  private getDefaultValueForConfig(configItem: ComponentConfig): any {
    switch (configItem.type) {
      case "checkbox":
        return false;
      case "color":
        return "#000000";
      case "select":
        return configItem.accepted_values?.[0] || "";
      case "image":
      case "video":
        return { src: "", alt: "" };
      case "customjs":
      case "customcss":
      case "text":
      default:
        return "";
    }
  }

  /**
   * Get the effective value for a device by applying the diff-based structure
   * Desktop contains all values, tablet = desktop + tablet changes, mobile = tablet + mobile changes
   */

  private getEffectiveValueForDevice(
    component: ResponsiveData,
    device: DeviceType,
    inputKey: string,
  ): any {
    let value: any = undefined;

    // Start with desktop values (base)
    if (component.desktop && Array.isArray(component.desktop)) {
      const desktopInput = component.desktop.find(
        (input: any) => input.inputKey === inputKey,
      );
      if (desktopInput) {
        value = desktopInput.value;
      }
    }

    // Apply tablet overrides if we're on tablet or mobile
    if (
      (device === "tablet" || device === "mobile") &&
      component.tablet &&
      Array.isArray(component.tablet)
    ) {
      const tabletInput = component.tablet.find(
        (input: any) => input.inputKey === inputKey,
      );
      if (tabletInput) {
        value = tabletInput.value;
      }
    }

    // Apply mobile overrides if we're on mobile
    if (
      device === "mobile" &&
      component.mobile &&
      Array.isArray(component.mobile)
    ) {
      const mobileInput = component.mobile.find(
        (input: any) => input.inputKey === inputKey,
      );
      if (mobileInput) {
        value = mobileInput.value;
      }
    }

    return value;
  }

  public initializeFormValuesWithMapping(
    section: Section,
    componentDefinitions: ComponentDefinition[],
    device: DeviceType,
    componentKey?: string, // Optional: if provided, only initialize for this specific component
  ): Record<string, any> {
    const formValues: Record<string, any> = {};
    const componentData = this.getComponentDataMap(section);
    const mapping = this.createComponentMapping(section);

    // If componentKey is provided, only initialize for that specific component
    if (componentKey && componentData[componentKey]) {
      const component = componentData[componentKey];
      const definition = componentDefinitions.find(
        (def) => def.slug === component.componentSlug,
      );

      if (definition) {
        for (const config of definition.config) {
          const fieldKey = this.createFieldKey(definition.slug, config.title);

          const value = this.getEffectiveValueForDevice(
            component,
            device,
            config.title,
          );

          formValues[fieldKey] =
            value !== undefined ? value : this.getDefaultValueForConfig(config);
        }
      }
      return formValues;
    }

    // Original behavior: initialize for all components (fallback for backward compatibility)
    for (const definition of componentDefinitions) {
      const mappedKeys = mapping.get(definition.slug) || [];
      if (mappedKeys.length === 0) continue;

      const dataKey = mappedKeys[0];
      const component = componentData[dataKey];
      if (!component) continue;

      for (const config of definition.config) {
        const fieldKey = this.createFieldKey(definition.slug, config.title);

        // Try to find the value using inputKey (which matches config.title in your structure)
        const value = this.getEffectiveValueForDevice(
          component,
          device,
          config.title,
        );

        formValues[fieldKey] =
          value !== undefined ? value : this.getDefaultValueForConfig(config);
      }
    }
    return formValues;
  }

  public updateFormValueWithMapping(
    section: Section,
    componentDefinitions: ComponentDefinition[],
    fieldKey: string,
    value: any,
    device: DeviceType,
    componentKey?: string, // Add optional componentKey parameter to specify which component to update
  ): Section {
    console.log("sectionDataService.updateFormValueWithMapping called:", {
      sectionId: section.id,
      fieldKey,
      value,
      device,
      componentKey,
      originalComponentData: section.component_data,
    });

    const { componentSlug, configTitle } = this.parseFieldKey(fieldKey);

    const newSection = this.deepClone(section);
    const componentData = this.getComponentDataMap(newSection);

    console.log("After deepClone and getComponentDataMap:", {
      newSectionId: newSection.id,
      componentDataKeys: Object.keys(componentData),
      componentData,
    });

    const mapping = this.createComponentMapping(newSection);

    let dataKey: string;

    // If componentKey is provided, use it directly (fixes the bug where multiple components share the same slug)
    if (componentKey && componentData[componentKey]) {
      dataKey = componentKey;
    } else {
      // Fallback to the old behavior for backward compatibility
      let mappedKeys = mapping.get(componentSlug);

      if (!mappedKeys || mappedKeys.length === 0) {
        const fallbackKey = `${componentSlug}-1`;
        mappedKeys = [fallbackKey];
        componentData[fallbackKey] = {
          desktop: [],
          tablet: [],
          mobile: [],
          name: fallbackKey,
          sectionID: newSection.id,
          type: "component",
          componentSlug,
        };
      }

      dataKey = mappedKeys[0];
    }

    const component = componentData[dataKey];

    if (!component) {
      console.warn("Component not found for dataKey:", dataKey);
      return newSection;
    }

    const definition = componentDefinitions.find(
      (def) => def.slug === componentSlug,
    );
    const config = definition?.config.find((c) => c.title === configTitle);
    let oldValue: any;

    // Get the current value before updating
    oldValue = this.getEffectiveValueForDevice(component, device, configTitle);

    console.log("Before update:", {
      component,
      oldValue,
      configTitle,
      device,
    });

    // Update the appropriate device array
    const updateDeviceArray = (
      deviceArray: any[],
      inputKey: string,
      newValue: any,
    ) => {
      const existingIndex = deviceArray.findIndex(
        (input: any) => input.inputKey === inputKey,
      );
      if (existingIndex >= 0) {
        deviceArray[existingIndex].value = newValue;
      } else {
        deviceArray.push({
          inputKey: inputKey,
          type: config?.type || "text",
          value: newValue,
        });
      }
    };

    // Ensure the device array exists
    if (!Array.isArray(component[device])) {
      component[device] = [];
    }

    // Update the value in the current device
    updateDeviceArray(component[device], configTitle, value);

    // If this is tablet or mobile, we need to check if the value is different from the base
    // If it's the same as the base, we can remove it from the diff array
    if (device === "tablet" || device === "mobile") {
      const baseDevice = device === "tablet" ? "desktop" : "tablet";
      const baseValue = this.getEffectiveValueForDevice(
        component,
        baseDevice,
        configTitle,
      );

      if (value === baseValue) {
        // Remove from current device array since it's the same as base
        const currentArray = component[device] as any[];
        const index = currentArray.findIndex(
          (input: any) => input.inputKey === configTitle,
        );
        if (index >= 0) {
          currentArray.splice(index, 1);
        }
      }
    }

    if (oldValue === undefined) {
      oldValue = config ? this.getDefaultValueForConfig(config) : undefined;
    }

    this.addToChangeHistory(newSection.id, {
      fieldKey,
      oldValue,
      newValue: value,
      device,
      timestamp: new Date().toISOString(),
    });

    // Ensure we create a new reference for component_data to trigger Redux updates
    newSection.component_data = { ...componentData };

    console.log("After update:", {
      updatedComponentData: newSection.component_data,
      componentDataKeys: Object.keys(newSection.component_data),
    });

    return newSection;
  }

  private addToChangeHistory(sectionId: string, change: ChangeHistoryEntry) {
    if (!this.changeHistory.has(sectionId)) {
      this.changeHistory.set(sectionId, []);
    }
    const history = this.changeHistory.get(sectionId)!;
    history.push(change);
    if (history.length > this.maxHistorySize) {
      history.shift();
    }
  }

  public getChangeHistory(sectionId: string): ChangeHistoryEntry[] {
    return this.changeHistory.get(sectionId) || [];
  }

  public clearChangeHistory(sectionId: string) {
    this.changeHistory.delete(sectionId);
  }
}

export const sectionDataService = new SectionDataService();
